import { useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchReviewQueue } from "./api";
import type { ReviewQueueResponse } from "./types";

export const reviewQueueKeys = {
  root: () => ["review-queue"] as const,
  pack: (workspaceId: string | null, packId: string | null) =>
    ["review-queue", workspaceId, packId] as const,
};

/**
 * Fetch the deterministic review queue for a pack. Enabled only when
 * both workspace and pack are known. Stale time is 30 s to keep the
 * founder's cursor stable during a session — invalidation on decision
 * refreshes on demand.
 */
export function useReviewQueue(
  workspaceId: string | null,
  packId: string | null,
  opts: { content_type?: string; platform?: string; limit?: number } = {},
) {
  return useQuery<ReviewQueueResponse>({
    queryKey: [
      ...reviewQueueKeys.pack(workspaceId, packId),
      opts.content_type ?? null,
      opts.platform ?? null,
      opts.limit ?? null,
    ] as const,
    queryFn: () => fetchReviewQueue(workspaceId!, packId!, opts),
    enabled: Boolean(workspaceId && packId),
    staleTime: 30_000,
  });
}

/** Invalidate the review queue for a specific pack. Used after every
 * successful transition inside the Review Session. */
export function useInvalidateReviewQueue() {
  const qc = useQueryClient();
  return (workspaceId: string, packId: string) =>
    qc.invalidateQueries({
      queryKey: reviewQueueKeys.pack(workspaceId, packId),
    });
}
